import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SubParcelDto } from '@forest/contracts';
import { api } from '@/lib/api-client';

/** Subpolygons of one parcel, in designation order. */
export function useSubParcels(parcelId: string | null | undefined) {
  return useQuery<{ items: SubParcelDto[] }>({
    queryKey: ['subparcels', 'byParcel', parcelId],
    queryFn: () =>
      api
        .get('subparcels', { searchParams: { parcelId: parcelId as string } })
        .json<{ items: SubParcelDto[] }>(),
    enabled: !!parcelId,
    staleTime: 30_000,
  });
}

export function useDeleteSubParcel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`subparcels/${id}`).json<SubParcelDto>(),
    onSuccess: () => {
      // The map layer and every parcel list share the `subparcels` prefix.
      void qc.invalidateQueries({ queryKey: ['subparcels'] });
      void qc.invalidateQueries({ queryKey: ['photos'] });
      // The polygon list carries a subpolygon count per row.
      void qc.invalidateQueries({ queryKey: ['parcels'] });
    },
  });
}

/** Subpolygons in no polygon (receptions surveyed outside every parcel). */
export function useUnattachedSubParcels(enabled: boolean) {
  return useQuery<{ items: SubParcelDto[] }>({
    queryKey: ['subparcels', 'unattached'],
    queryFn: () =>
      api.get('subparcels', { searchParams: { unattached: 'true' } }).json<{ items: SubParcelDto[] }>(),
    enabled,
    staleTime: 30_000,
  });
}

export function useUnattachedSubParcelCount() {
  return useQuery<{ count: number }>({
    queryKey: ['subparcels', 'unattached', 'count'],
    queryFn: () => api.get('subparcels/unattached/count').json<{ count: number }>(),
    staleTime: 60_000,
  });
}
